'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import PrimaryButton from '@/components/Form/Buttons/PrimaryButton';
import { Loader } from 'rsuite';
import clsx from 'clsx';
import { BACKEND_UNAVAILABLE_ERROR } from '@/lib/auth-errors';

type Props = {
  providers: string[];
};

export default function LoginForm({ providers }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loginWithEmail = providers.includes('email');
  const loginWithGoogle = providers.includes('google');
  const loginWithMicrosoft = providers.includes('microsoft');

  const isOauthCallback = searchParams.get('oauth') === '1';
  const sessionExpired = searchParams.get('expired') === '1';

  useEffect(() => {
    if (searchParams.get('oauth') !== '1') return;

    setIsLoading(true);
    signIn('strapi-oauth', { trigger: '1', redirect: false })
      .then((result) => {
        if (result?.ok) {
          router.push('/');
        } else {
          setError('Error al iniciar sesión. Por favor, inténtalo de nuevo.');
          setIsLoading(false);
        }
      })
      .catch(() => {
        setError('Error al iniciar sesión. Por favor, inténtalo de nuevo.');
        setIsLoading(false);
      });
  }, [searchParams, router]);

  useEffect(() => {
    if (searchParams.get('error')) {
      setError('Error al iniciar sesión. Por favor, inténtalo de nuevo.');
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        if (result.error === BACKEND_UNAVAILABLE_ERROR) {
          setError('El servicio no está disponible en este momento.');
        } else {
          setError('Correo o contraseña incorrectos.');
        }
      } else {
        router.push('/');
      }
    } catch {
      setError('Ocurrió un error. Por favor, inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isOauthCallback && !error) {
    return (
      <>
        <Image
          className="m-auto mt-6"
          src={'/images/logo/logo.svg'}
          width={180}
          height={36}
          alt="Logo"
        />

        <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark m-6 max-w-lg rounded-xs border bg-white md:m-auto">
          <div className="flex w-full flex-col items-center gap-4 p-10">
            <Loader size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Iniciando sesión...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Image
        className="m-auto mt-6"
        src={'/images/logo/logo.svg'}
        width={180}
        height={36}
        alt="Logo"
      />

      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark m-6 max-w-lg rounded-xs border bg-white md:m-auto">
        <div className="w-full p-6 md:p-10">
          {sessionExpired && !error && (
            <div className="mb-6 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Tu sesión expiró. Por favor, inicia sesión nuevamente.
            </div>
          )}

          {(loginWithGoogle || loginWithMicrosoft) && (
            <div className="mb-4 grid grid-cols-1 gap-3">
              {loginWithGoogle && (
                <Link
                  className={clsx(
                    'w-full px-[12px] py-[11px]',
                    'flex flex-row items-center gap-[12px] rounded-sm',
                    'justify-center text-center font-medium',
                    'hover:bg-opacity-90 disabled:cursor-default disabled:opacity-50',
                    'border-stroke border bg-slate-50',
                    'dark:bg-boxdark dark:border-strokedark',
                  )}
                  href={'/api/auth/connect/google'}
                >
                  <Image
                    src={'/images/logo/logo-google.svg'}
                    width={20}
                    height={20}
                    alt="Google Logo"
                  />
                  Entrar con Google
                </Link>
              )}
              {loginWithMicrosoft && (
                <Link
                  className={clsx(
                    'w-full px-[12px] py-[11px]',
                    'flex flex-row items-center gap-[12px] rounded-sm',
                    'justify-center text-center font-medium',
                    'hover:bg-opacity-90 disabled:cursor-default disabled:opacity-50',
                    'border-stroke border bg-slate-50',
                    'dark:bg-boxdark dark:border-strokedark',
                  )}
                  href={'/api/auth/connect/microsoft'}
                >
                  <Image
                    src={'/images/logo/logo-microsoft.svg'}
                    width={20}
                    height={20}
                    alt="Microsoft Logo"
                  />
                  Entrar con Microsoft
                </Link>
              )}
            </div>
          )}

          {loginWithEmail && (
            <>
              {(loginWithGoogle || loginWithMicrosoft) && (
                <div className="relative py-3 sm:py-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="border-stroke dark:border-strokedark w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="dark:bg-boxdark bg-white px-5 py-2 text-gray-400">
                      O
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      required
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-stroke focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary w-full rounded-lg border bg-transparent py-4 pr-10 pl-6 text-black outline-hidden focus-visible:shadow-none dark:text-white"
                    />
                    <span className="absolute top-4 right-4">
                      <svg
                        className="fill-current"
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.5">
                          <path
                            d="M19.2516 3.30005H2.75156C1.58281 3.30005 0.585938 4.26255 0.585938 5.46567V16.6032C0.585938 17.7719 1.54844 18.7688 2.75156 18.7688H19.2516C20.4203 18.7688 21.4172 17.8063 21.4172 16.6032V5.4313C21.4172 4.26255 20.4203 3.30005 19.2516 3.30005ZM19.2516 4.84692C19.2859 4.84692 19.3203 4.84692 19.3547 4.84692L11.0016 10.2094L2.64844 4.84692C2.68281 4.84692 2.71719 4.84692 2.75156 4.84692H19.2516ZM19.2516 17.1532H2.75156C2.40781 17.1532 2.13281 16.8782 2.13281 16.5344V6.35942L10.1766 11.5157C10.4172 11.6875 10.6922 11.7563 10.9672 11.7563C11.2422 11.7563 11.5172 11.6875 11.7578 11.5157L19.8016 6.35942V16.5688C19.8703 16.9125 19.5953 17.1532 19.2516 17.1532Z"
                            fill=""
                          />
                        </g>
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      required
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="border-stroke focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary w-full rounded-lg border bg-transparent py-4 pr-10 pl-6 outline-hidden focus-visible:shadow-none dark:text-white"
                    />
                    <span className="absolute top-4 right-4">
                      <svg
                        className="fill-current"
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.5">
                          <path
                            d="M16.1547 6.80626V5.91251C16.1547 3.16251 14.0922 0.825009 11.4797 0.618759C10.0359 0.481259 8.59219 0.996884 7.52656 1.95938C6.46094 2.92188 5.84219 4.29688 5.84219 5.70626V6.80626C3.84844 7.18438 2.33594 8.93751 2.33594 11.0688V17.2906C2.33594 19.5594 4.19219 21.3813 6.42656 21.3813H15.5016C17.7703 21.3813 19.6266 19.525 19.6266 17.2563V11C19.6609 8.93751 18.1484 7.21876 16.1547 6.80626ZM8.55781 3.09376C9.31406 2.40626 10.3109 2.06251 11.3422 2.16563C13.1641 2.33751 14.6078 3.98751 14.6078 5.91251V6.70313H7.38906V5.67188C7.38906 4.70938 7.80156 3.78126 8.55781 3.09376ZM18.1141 17.2906C18.1141 18.7 16.9453 19.8688 15.5359 19.8688H6.46094C5.05156 19.8688 3.91719 18.7344 3.91719 17.325V11.0688C3.91719 9.52189 5.15469 8.28438 6.70156 8.28438H15.2953C16.8422 8.28438 18.1141 9.52188 18.1141 11V17.2906Z"
                            fill=""
                          />
                          <path
                            d="M10.9977 11.8594C10.5852 11.8594 10.207 12.2031 10.207 12.65V16.2594C10.207 16.6719 10.5508 17.05 10.9977 17.05C11.4102 17.05 11.7883 16.7063 11.7883 16.2594V12.6156C11.7883 12.2031 11.4102 11.8594 10.9977 11.8594Z"
                            fill=""
                          />
                        </g>
                      </svg>
                    </span>
                  </div>
                </div>

                <PrimaryButton
                  disabled={isLoading}
                  type="submit"
                  className="w-full"
                >
                  {isLoading && (
                    <ArrowPathIcon className={'h-4 w-4 animate-spin'} />
                  )}
                  {isLoading ? 'Ingresando...' : 'Login'}
                </PrimaryButton>
              </form>
            </>
          )}

          {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
        </div>
      </div>
    </>
  );
}
